import {showMessage} from 'exsportia-components'
import {call, put, select, takeLeading} from 'redux-saga/effects'

import {API} from '../../networking/http'
import {basicActions as basicGlobalActions} from '../global/actions'
import {basicActions as basicUserActions} from '../user/actions'
import {resType} from '../user/saga'
import {basicSelectors as userSelectors} from '../user/selectors'
import {
  cancelBooking,
  CancelBookingPayloadType,
  checkClassPasscode,
  CheckClassPasscodePayloadType,
  checkServicePasscode,
  CheckServicePasscodePayloadType,
  checkSubscriptionPasscode,
  CheckSubscriptionPasscodePayloadType,
  createWaitingListBookingRequest,
  getWaitingListBookingsRequest,
  unjoinWaitingListRequest,
} from './actions'
import {setPrivatePasscode} from './private-passcodes'
import {WaitingListBooking} from './types'

export function* checkClassPasscodeRequest(payload: CheckClassPasscodePayloadType) {
  const {classId, code, callback, rejectCallback} = payload
  try {
    const endpoint = 'classes/private'
    const res = (yield call(API.post, endpoint, {classId, password: code})) as resType
    if (res.status === 200) {
      setPrivatePasscode(classId, code)
    }
    if (res.status === 200 && typeof callback === 'function') {
      callback()
    } else {
      rejectCallback?.()
    }
  } catch (error: unknown) {
    if (typeof rejectCallback === 'function') {
      rejectCallback()
    }
    console.log(error)
  }
}

export function* checkServicePasscodeRequest(payload: CheckServicePasscodePayloadType) {
  const {sportId, password, callback, rejectCallback} = payload
  try {
    const endpoint = 'areas/private'
    console.log(endpoint, '!!!!!')
    const res = (yield call(API.post, endpoint, {sportId, password})) as resType
    if (res.status === 200) {
      setPrivatePasscode(sportId, password)
    }
    if (res.status === 200 && typeof callback === 'function') {
      callback()
    } else {
      if (typeof rejectCallback === 'function') {
        rejectCallback()
      }
    }
  } catch (error: unknown) {
    if (typeof rejectCallback === 'function') {
      rejectCallback()
    }
    console.log(error)
  }
}

export function* checkSubscriptionPasscodeRequest(payload: CheckSubscriptionPasscodePayloadType) {
  const {membershipId, password, callback, rejectCallback} = payload
  try {
    const endpoint = 'memberships/private'
    const res = (yield call(API.post, endpoint, {membershipId, password})) as resType
    if (res.status === 200 && typeof callback === 'function') {
      callback()
    } else {
      if (typeof rejectCallback === 'function') {
        rejectCallback()
      }
    }
  } catch (error: unknown) {
    if (typeof rejectCallback === 'function') {
      rejectCallback()
    }
    console.log(error)
  }
}

export function* cancelBookingRequest(payload: CancelBookingPayloadType) {
  const {bookingId, callback, rejectCallback, l} = payload
  try {
    const endpoint = `bookings/${bookingId}/cancel`
    const res = (yield call(API.put, endpoint)) as resType
    if (res.status === 200) {
      showMessage(l('cancelled', {caseAction: 'capitalize'}), 'success')
    }
    if (typeof callback === 'function') {
      callback()
    }
  } catch (error: unknown) {
    // @ts-ignore
    // eslint-disable-next-line
    if (error?.response?.status === 400) {
      // @ts-ignore
      // eslint-disable-next-line
      showMessage(error?.response?.data?.message || l('something_went_wrong'), 'error')
    } else {
      showMessage(l('something_went_wrong', {caseAction: 'capitalize'}), 'error')
      console.log(error)
    }
    if (typeof rejectCallback === 'function') {
      rejectCallback()
    }
  }
}

export function* handleCreateWaitingList({payload}: ReturnType<typeof createWaitingListBookingRequest>) {
  const {data, callback, l} = payload
  yield put(basicGlobalActions.isLoading(true))
  try {
    type WaitingBookingResponse = Omit<resType, 'data'> & {
      data?: {
        uid?: string
        message?: string
      }
    }
    const res = (yield call(API.post, 'waitingBookings', data)) as WaitingBookingResponse
    if (res.status === 200) {
      // Manual Update: Specifically us 'slot' ke liye userInWaitingList property ko true kar diya jata hai.
      const currentWaitingList = (yield select(userSelectors.waitingListBookings())) as WaitingListBooking[]
      const waitingBookingId = res.data?.uid || 'temp_id'

      yield put(
        basicUserActions.waitingListBookings([
          ...currentWaitingList,
          {
            ...data,
            uid: waitingBookingId,
          },
        ]),
      )

      callback(waitingBookingId)
      yield put(
        getWaitingListBookingsRequest({
          venueId: data.venueId,
          memberId: data.memberId ?? '',
          l,
        }),
      )

      showMessage(l('youAreaOnTheWaitingList', {caseAction: 'capitalize'}), 'success')
    }
  } catch (error) {
    showMessage(l('something_went_wrong', {caseAction: 'capitalize'}), 'error')
    console.log(error)
  } finally {
    yield put(basicGlobalActions.isLoading(false))
  }
}

export function* handleUnjoinWaitingList({payload}: ReturnType<typeof unjoinWaitingListRequest>) {
  const {waitingBookingId, memberId, callback, l} = payload
  yield put(basicGlobalActions.isLoading(true))
  try {
    const res = (yield call(API.put, `waitingBookings/client/${waitingBookingId}/cancel`, {memberId})) as resType
    if (res.status === 200) {
      callback()
      // Manual Update: Refresh the waiting list after unjoining
      const currentWaitingList = (yield select(userSelectors.waitingListBookings())) as WaitingListBooking[]
      yield put(
        basicUserActions.waitingListBookings(
          currentWaitingList.filter((b) => b.uid !== waitingBookingId && b.id !== waitingBookingId),
        ),
      )
      showMessage(l('you_just_leave_waiting_list', {caseAction: 'capitalize'}), 'warn')
    }
  } catch (error) {
    showMessage(l('something_went_wrong', {caseAction: 'capitalize'}), 'error')
    console.log(error)
  } finally {
    yield put(basicGlobalActions.isLoading(false))
  }
}

export function* handleGetWaitingListBookings({payload}: ReturnType<typeof getWaitingListBookingsRequest>) {
  const {venueId, memberId, callback} = payload
  try {
    type GetWaitingListResponse = Omit<resType, 'data'> & {
      data?: Record<string, WaitingListBooking> | WaitingListBooking[]
    }
    const res = (yield call(API.get, 'waitingBookings/by-session-map', {
      params: {
        venueId,
        memberId,
      },
    })) as GetWaitingListResponse
    if (res.status === 200) {
      const bookingData = Array.isArray(res.data) ? res.data : Object.values(res.data || {})
      yield put(basicUserActions.waitingListBookings(bookingData))
      callback?.()
    }
  } catch (error) {
    console.log(error)
  }
}

export function* bookingWatcherSaga() {
  yield takeLeading(checkClassPasscode, function* (action) {
    yield checkClassPasscodeRequest(action.payload)
  }),
    yield takeLeading(checkServicePasscode, function* (action) {
      yield checkServicePasscodeRequest(action.payload)
    }),
    yield takeLeading(checkSubscriptionPasscode, function* (action) {
      yield checkSubscriptionPasscodeRequest(action.payload)
    }),
    yield takeLeading(cancelBooking, function* (action) {
      yield cancelBookingRequest(action.payload)
    }),
    yield takeLeading(createWaitingListBookingRequest, handleCreateWaitingList),
    yield takeLeading(unjoinWaitingListRequest, handleUnjoinWaitingList),
    yield takeLeading(getWaitingListBookingsRequest, handleGetWaitingListBookings)
}
